import {
  NotificationContext,
  useErrorNotification,
} from "@common/components/Notification";
import { getRole, saveRole } from "@services/user";
import { AccountsVisibility, Role } from "@services/user/types";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import RoleContext from "./Context";

interface RoleProviderProps {
  onCancel: () => void;
  roleId?: number;
}

const defaultRole: Partial<Role> = {
  name: "",
  description: "",
  rolePermissions: {
    admin: true,
    accountConfiguration: "NoAccess",
    accounts: "NoAccess",
    adminAccountsVisibility: AccountsVisibility.HomeAccountOnly,
    analyticsReports: "NoAccess",
    assignProducer: false,
    assignRolesToUsers: false,
    attachments: "NoAccess",
    devices: "NoAccess",
    globalConfiguration: "NoAccess",
    linkAccountsToUsers: false,
    locations: "NoAccess",
    meetings: "NoAccess",
    meetingsElogsAndRevisions: "NoAccess",
    produceMeetings: false,
    reports: "NoAccess",
    rolesCapabilities: "NoAccess",
    rooms: "NoAccess",
    tickets: "NoAccess",
    users: "NoAccess",
  },
  summary: {},
};

const RoleProvider = ({
  roleId,
  onCancel,
  children,
}: PropsWithChildren<RoleProviderProps>): React.JSX.Element => {
  const { t } = useTranslation();
  const dispatchError = useErrorNotification();
  const { dispatchNotification } = useContext(NotificationContext);

  const [role, setRole] = useState(defaultRole);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModified, setIsModified] = useState<boolean>(false);
  const isValid = Boolean(role?.name);

  const refetch = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsModified(false);
      if (roleId !== undefined) {
        var role = await getRole(roleId);
        setRole(role);
      } else {
        setRole(defaultRole);
      }
    } catch (e) {
      dispatchError(e);
    } finally {
      setIsLoading(false);
    }
  }, [roleId, dispatchError, setIsLoading, setIsModified]);

  useEffect(() => {
    refetch();
  }, [roleId]);

  const saveChanges = async () => {
    try {
      setIsLoading(true);
      await saveRole(role);
      onCancel();
    } catch (e) {
      dispatchError(e);
    } finally {
      setIsLoading(false);
    }
  };

  const updateRole = (value: React.SetStateAction<Partial<Role>>) => {
    setIsModified(true);
    setRole(value);
  };
  useEffect(() => {
    if (
      role?.rolePermissions?.produceMeetings === true &&
      role?.rolePermissions?.tickets === "NoAccess"
    ) {
      dispatchNotification({
        message: t(
          "Role with can produce meetings ability must have at least View Only visibility on component Tickets."
        ),
        title: t("Warning"),
        duration: 7_000,
      });
    }
  }, [role?.rolePermissions?.produceMeetings]);

  return (
    <RoleContext.Provider
      value={{
        role,
        setRole: updateRole,
        saveChanges,
        cancelChanges: () => {
          setIsModified(false);
          setRole(defaultRole);
          onCancel();
        },
        isLoading,
        isValid,
        isModified,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};

export default RoleProvider;
